import { AttachmentFile, ContractSuretyType, FileType, SuretyAccountId } from "@inrev/common";
import { accountQueryKey } from "@portal/api/account";
import { FormSection } from "@portal/components/form";
import { Spinner } from "@portal/components/primitives";
import { AttachmentUpload } from "@portal/components/ui/Upload";
import type { QueryKey } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useGetFileUrl } from "../../../api";

const BondFilesUpload = ({
	bondType,
	attachments,
	accountId,
	allowDelete,
	onChange,
	baseUrl,
	allowUpload,
	additionalInvalidateQueryKeys,
	getSummaryAction,
	isLoading = false,
}: {
	bondType: ContractSuretyType;
	attachments: AttachmentFile[];
	accountId: SuretyAccountId;
	allowDelete: boolean;
	baseUrl: string;
	onChange: (attachments: AttachmentFile[]) => void;
	allowUpload?: boolean;
	additionalInvalidateQueryKeys?: QueryKey[];
	getSummaryAction?: (id: string) => (() => void) | undefined;
	isLoading?: boolean;
}) => {
	const { getFileUrl } = useGetFileUrl();

	const fileType: FileType =
		bondType === ContractSuretyType.bid ? FileType.bid_package : FileType.contract;
	const files = attachments.filter((attachment) => attachment.file.types.includes(fileType));

	const label: string = bondType === ContractSuretyType.bid ? "RFP / Bid Package" : "Contract";
	const missingFile = !isLoading && files.length === 0;
	const subHeader =
		missingFile &&
		`Please upload a copy of the ${bondType === ContractSuretyType.bid ? "RFP / Bid Package" : "contract"}`;

	const filter = useCallback(
		(attachment: AttachmentFile) => attachment.file.types.includes(fileType),
		[fileType],
	);

	const allowedTypesAndLabels = useMemo(() => ({ [fileType]: label }), [fileType, label]);

	const invalidateQueryKeys = useMemo(
		() => [accountQueryKey(accountId), ...(additionalInvalidateQueryKeys ?? [])] as QueryKey[],
		[accountId, additionalInvalidateQueryKeys],
	);

	const onDownload = useCallback(
		(attachmentId: string) =>
			getFileUrl({
				baseUrl: `${baseUrl}/attachments/${attachmentId}`,
				queryKey: ["bondRequestAttachments", attachmentId, true],
				asDownload: true,
			}),
		[getFileUrl, baseUrl],
	);

	return (
		<FormSection id="contract-section" header={label} subHeader={subHeader} partial={missingFile}>
			{isLoading ? (
				<div
					className="flex items-center justify-center h-[92px] text-tertiary"
					data-testid="bond-files-loading"
				>
					<Spinner size="lg" />
				</div>
			) : (
				<AttachmentUpload
					value={attachments}
					filter={filter}
					onChange={onChange}
					url={`${baseUrl}/attachments`}
					allowedTypesAndLabels={allowedTypesAndLabels}
					invalidateQueryKeys={invalidateQueryKeys}
					onDownload={onDownload}
					getSummaryAction={getSummaryAction}
					baseUrl={`${baseUrl}/attachments`}
					queryKey={"bondRequestAttachments"}
					enableTypeSelection={false}
					showTabs={false}
					allowDelete={allowDelete}
					maxTypesPerFile={1}
					hideChecklist
					enableUpload={allowUpload}
					analyticsContext="bond-request-attachments"
					trackingPrefix="bond-request-attachments-upload"
				/>
			)}
		</FormSection>
	);
};

export default BondFilesUpload;
